/**
 * The bar across the top of a kiosk session (estate change E2).
 *
 * Three jobs, in the order they matter to somebody standing at a shared
 * machine in a warehouse:
 *
 *   1 · **"Is this me?"** — their own face and name, large. On a shared PC
 *       that is a real question, and a photo answers it faster than any
 *       wording can.
 *   2 · **"Take your card out when you've finished."** The session ends when
 *       the card leaves, so the card is the thing to remember, not a button.
 *   3 · **Email**, one tap away and always on screen. Frontline staff are
 *       getting a mailbox they have never had; burying it two clicks deep in a
 *       launcher would waste the whole exercise.
 *
 * ⚠️ There is deliberately no Sign out here. On a kiosk the way to end a
 * session is to take the card out (hard constraint 1), and offering a button
 * that does the same thing invites somebody to press it and walk off **leaving
 * the card in the slot** — which is the one failure this design exists to
 * prevent.
 */
import { api, store } from '@isg/ui-kit';
import { t } from '../i18n/index.js';

// Where Email goes when the app registry cannot tell us. ⚠️ A fallback rather
// than a guess: the registry is the single source for launcher URLs, but the
// Email button is the reason a frontline person has a kiosk at all, and a
// missing registry row must not be the thing that removes it.
const OUTLOOK_FALLBACK = 'https://outlook.office.com/mail/';

function escHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

async function emailUrl() {
  try {
    const data = await api.get('/apps');
    const apps = Array.isArray(data) ? data : (data.apps || []);
    const found = apps.find((a) => ['outlook', 'email', 'mail'].includes(
      String(a.slug || '').toLowerCase()));
    if (found?.base_url) return found.base_url;
  } catch {
    /* the registry is unreachable; the button still has to work */
  }
  return OUTLOOK_FALLBACK;
}

export function createKioskBar() {
  const user = store.get('user') || {};
  const name = user.display_name || user.username || '';
  const initial = (name.charAt(0) || '?').toUpperCase();

  const bar = document.createElement('div');
  bar.className = 'kiosk-bar';
  bar.setAttribute('role', 'status');
  bar.innerHTML = `
    <div class="kiosk-bar-who">
      ${user.avatar_url
        ? `<div class="kiosk-avatar"><img src="${escHtml(user.avatar_url)}" alt=""></div>`
        : `<div class="kiosk-avatar">${escHtml(initial)}</div>`}
      <div class="kiosk-bar-text">
        <div class="kiosk-bar-name">${escHtml(name)}</div>
        <div class="kiosk-bar-hint">${escHtml(t('kiosk.takeCardOut'))}</div>
      </div>
    </div>
    <a class="kiosk-email" id="kiosk-email" href="${OUTLOOK_FALLBACK}"
       target="_blank" rel="noopener noreferrer">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/>
      </svg>
      <span>${escHtml(t('kiosk.email'))}</span>
    </a>`;

  // Resolved after paint: the bar must appear instantly, and the fallback URL
  // in the markup means the button works even if this never returns.
  emailUrl().then((url) => {
    const link = bar.querySelector('#kiosk-email');
    if (link) link.href = url;
  });

  return bar;
}
